import { saveAs } from "file-saver";

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  UnderlineType,
  TableCell,
  WidthType,
  ImageRun,
  ShadingType,
  AlignmentType,
} from "docx";
import { buildExperimentTimelineOverview } from "./timelineOverview";
import { buildSection30Summary } from "./section30Summary";
import {
  getExperimentSeverityWarnings,
  getMaxDeclaredSeverity,
} from "./severityValidation";
import { getStrainColumns, getGroupCount } from "../components/Section29Form";

const sectionTitle = (text) =>
  new Paragraph({
    spacing: { before: 300, after: 100 },
    children: [
      new TextRun({
        text,
        bold: true,
        underline: { type: UnderlineType.SINGLE },
        size: 28,
      }),
    ],
  });

// Helper: build the Section 29 animal count table
const buildSection29Table = (formData) => {
  const strainColumns = getStrainColumns(formData);

  const boldCell = (text, options = {}) =>
    new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text, bold: true })], alignment: AlignmentType.CENTER })],
      ...options,
    });

  const plainCell = (text, options = {}) =>
    new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: String(text) })], alignment: AlignmentType.CENTER })],
      ...options,
    });

  const pinkShading = {
    shading: { fill: "FCE4EC", type: ShadingType.SOLID, color: "auto" },
  };

  const headerRows = [
    new TableRow({
      children: [
        boldCell("Objective"),
        boldCell("Experiments"),
        boldCell("Groups"),
        ...strainColumns.map((col) => boldCell(col.label)),
        boldCell("Total"),
      ],
    }),
  ];

  const dataRows = [];
  const grandTotals = strainColumns.map(() => 0);

  formData.forEach((exp, expIndex) => {
    const objectiveLabel = exp.objectiveLabel || `Objective ${expIndex + 1}`;
    const objectiveRowCount = formData
      .filter((item, itemIndex) => (item.objectiveLabel || `Objective ${itemIndex + 1}`) === objectiveLabel)
      .reduce((sum, item) => {
        const groupCount = item.groups?.length || 0;
        return sum + (groupCount > 0 ? groupCount : 1);
      }, 0);
    const firstObjectiveIndex = formData.findIndex(
      (item, itemIndex) => (item.objectiveLabel || `Objective ${itemIndex + 1}`) === objectiveLabel
    );
    const groups = exp.groups || [];
    const expLabel = exp.experimentObjective || `Experiment ${expIndex + 1}`;

    if (groups.length === 0) {
      const cells = [];
      if (expIndex === firstObjectiveIndex) {
        cells.push(
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: objectiveLabel, bold: true, color: "CC0044" })] })],
            rowSpan: objectiveRowCount,
          })
        );
      }
      cells.push(
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: expLabel, bold: true, color: "CC0044" })] })],
        }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "No groups defined", italics: true })] })] }),
      );
      strainColumns.forEach(() => cells.push(plainCell("")));
      cells.push(plainCell(""));
      dataRows.push(new TableRow({ children: cells }));
    } else {
      groups.forEach((group, gi) => {
        const colCounts = strainColumns.map(({ id }) => parseInt(getGroupCount(group, id), 10) || 0);
        const rowTotal = colCounts.reduce((a, b) => a + b, 0);
        colCounts.forEach((count, ci) => { grandTotals[ci] += count; });

        const cells = [];
        if (gi === 0 && expIndex === firstObjectiveIndex) {
          cells.push(
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: objectiveLabel, bold: true, color: "CC0044" })] })],
              rowSpan: objectiveRowCount,
            })
          );
        }
        if (gi === 0) {
          cells.push(
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: expLabel, bold: true, color: "CC0044" })] })],
              rowSpan: groups.length,
            })
          );
        }
        cells.push(new TableCell({ children: [new Paragraph(group.groupName || "")] }));
        colCounts.forEach((count) => cells.push(plainCell(String(count))));
        cells.push(
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(rowTotal), bold: true })], alignment: AlignmentType.CENTER })] }),
        );

        dataRows.push(new TableRow({ children: cells }));
      });
    }
  });

  const grandTotalAll = grandTotals.reduce((a, b) => a + b, 0);

  const summaryValueCells = [
    new TableCell({ children: [new Paragraph("")], columnSpan: 3, ...pinkShading }),
    ...grandTotals.map((total) =>
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(total), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading })
    ),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(grandTotalAll), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
  ];

  const summaryLabelCells = [
    new TableCell({ children: [new Paragraph("")], columnSpan: 3, ...pinkShading }),
    ...strainColumns.map((col) =>
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `Total ${col.label}`, bold: true, italics: true })], alignment: AlignmentType.CENTER })], ...pinkShading })
    ),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Total application", bold: true, italics: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
  ];

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [...headerRows, ...dataRows, new TableRow({ children: summaryValueCells }), new TableRow({ children: summaryLabelCells })],
  });
};

const buildTimelineOverviewTable = (overview) => {
  const labelCell = (text) =>
    new TableCell({
      width: { size: 34, type: WidthType.PERCENTAGE },
      children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })],
      shading: { fill: "F6F8FA", type: ShadingType.SOLID, color: "auto" },
    });

  const valueCell = (text) =>
    new TableCell({
      width: { size: 66, type: WidthType.PERCENTAGE },
      children: [new Paragraph(String(text || "Not specified"))],
    });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ children: [labelCell("Duration"), valueCell(`${overview.durationDays || 0} days`)] }),
      new TableRow({ children: [labelCell("Cohorts"), valueCell(overview.cohorts || "Not specified")] }),
      new TableRow({ children: [labelCell("Repetitions"), valueCell(overview.repetitions || "Not specified")] }),
    ],
  });
};

const buildSection36Table = (formData) => {
  const strainColumns = getStrainColumns(formData);
  const SD_KEYS = ["sd0", "sd1", "sd2", "sd3"];

  const centeredCell = (text, options = {}) =>
    new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: String(text) })], alignment: AlignmentType.CENTER })],
      ...options,
    });

  const boldCenteredCell = (text, options = {}) =>
    new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: String(text), bold: true })], alignment: AlignmentType.CENTER })],
      ...options,
    });

  const pinkShading = {
    shading: { fill: "FCE4EC", type: ShadingType.SOLID, color: "auto" },
  };

  const headerRows = [
    new TableRow({
      children: [
        boldCenteredCell("Objective"),
        boldCenteredCell("Experiment"),
        ...strainColumns.map((col) => boldCenteredCell(col.label)),
        boldCenteredCell("Total Experiment"),
        boldCenteredCell("SD0"),
        boldCenteredCell("SD1"),
        boldCenteredCell("SD2"),
        boldCenteredCell("SD3"),
      ],
    }),
  ];

  const rows = [...headerRows];

  const grandTotals = strainColumns.map(() => 0);
  let totalSD0 = 0, totalSD1 = 0, totalSD2 = 0, totalSD3 = 0;

  formData.forEach((exp, index) => {
    const objectiveLabel = exp.objectiveLabel || `Objective ${index + 1}`;
    const objectiveCount = formData.filter(
      (item, itemIndex) => (item.objectiveLabel || `Objective ${itemIndex + 1}`) === objectiveLabel
    ).length;
    const firstObjectiveIndex = formData.findIndex(
      (item, itemIndex) => (item.objectiveLabel || `Objective ${itemIndex + 1}`) === objectiveLabel
    );
    const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
    const groups = exp.groups || [];
    const colCounts = strainColumns.map(({ id }) =>
      groups.reduce((sum, g) => sum + (parseInt(getGroupCount(g, id), 10) || 0), 0)
    );
    const totalExperiment = colCounts.reduce((a, b) => a + b, 0);
    colCounts.forEach((count, ci) => { grandTotals[ci] += count; });

    const distribution = exp.severityDistribution || {};
    const sd0 = strainColumns.reduce((sum, { id }) => sum + (parseInt((distribution.sd0 || {})[id], 10) || 0), 0);
    const sd1 = strainColumns.reduce((sum, { id }) => sum + (parseInt((distribution.sd1 || {})[id], 10) || 0), 0);
    const sd2 = strainColumns.reduce((sum, { id }) => sum + (parseInt((distribution.sd2 || {})[id], 10) || 0), 0);
    const sd3 = strainColumns.reduce((sum, { id }) => sum + (parseInt((distribution.sd3 || {})[id], 10) || 0), 0);
    totalSD0 += sd0; totalSD1 += sd1; totalSD2 += sd2; totalSD3 += sd3;

    const rowCells = [];
    if (index === firstObjectiveIndex) {
      rowCells.push(
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: objectiveLabel, bold: true, color: "CC0044" })] })],
          rowSpan: objectiveCount,
        })
      );
    }

    rowCells.push(new TableCell({ children: [new Paragraph(expLabel)] }));
    colCounts.forEach((count) => rowCells.push(centeredCell(count)));
    rowCells.push(
      boldCenteredCell(totalExperiment),
      centeredCell(sd0),
      centeredCell(sd1),
      centeredCell(sd2),
      centeredCell(sd3),
    );

    rows.push(new TableRow({ children: rowCells }));
  });

  const totalGrandAll = grandTotals.reduce((a, b) => a + b, 0);

  const totalsValueCells = [
    new TableCell({ children: [new Paragraph("")], columnSpan: 2, ...pinkShading }),
    ...grandTotals.map((total) =>
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(total), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading })
    ),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(totalGrandAll), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(totalSD0), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(totalSD1), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(totalSD2), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(totalSD3), bold: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
  ];

  const totalsLabelCells = [
    new TableCell({ children: [new Paragraph("")], columnSpan: 2, ...pinkShading }),
    ...strainColumns.map((col) =>
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `Total ${col.label}`, bold: true, italics: true })], alignment: AlignmentType.CENTER })], ...pinkShading })
    ),
    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Total application", bold: true, italics: true })], alignment: AlignmentType.CENTER })], ...pinkShading }),
    ...SD_KEYS.map((key) =>
      new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `Total ${key.toUpperCase()}`, bold: true, italics: true })], alignment: AlignmentType.CENTER })], ...pinkShading })
    ),
  ];

  rows.push(
    new TableRow({ children: totalsValueCells }),
    new TableRow({ children: totalsLabelCells }),
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows,
  });
};

const generateFullDocx = async (formData, projectName = "Projet", timelineImages = []) => {
  const children = [];

  if (!Array.isArray(formData)) {
    console.error("generateFullDocx: formData is not an array", formData);
    return;
  }

  // Section 20
  children.push(sectionTitle("Section 20: Objective of the experiment and background"));
  children.push(
    new Paragraph(
      "Briefly describe (1) the objective of the project, (2) Current state of research, (3) Anticipated knowledge to be gained."
    )
  );
  // Section 22
  children.push(sectionTitle("Section 22: Hypothesis"));
  children.push(
    new Paragraph(
      "Formulate the research question to be answered in the experiment or the hypothesis (hypotheses) to be tested (confirmatory study)"
    )
  );

  formData.forEach((exp, index) => {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `- Experiment ${index + 1}:`,
            bold: true,
            color: "FF0000",
          }),
        ],
      })
    );
  });

  // Section 23
  children.push(sectionTitle("Section 23: Course of the experiments: Schematic representation"));
  children.push(
    new Paragraph(
      "Description of the course of the experiment from a temporal perspective. Representation of the course of the experiment or individual steps (e.g. flow chart, workflow diagram, table). To include: animal model, animal groups, and the overall duration of the experiment for each group."
    )
  );
  formData.forEach((exp, index) => {
    const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
    const overview = buildExperimentTimelineOverview(exp, index);
    children.push(
      new Paragraph({
        children: [new TextRun({ text: expLabel, bold: true, color: "CC0044" })],
        spacing: { before: 150, after: 50 },
      })
    );
    children.push(buildTimelineOverviewTable(overview));
    children.push(new Paragraph(""));
    const imgData = timelineImages[index];
    if (imgData && imgData.data) {
      const maxWidth = 620;
      const scale = Math.min(1, maxWidth / imgData.width);
      const displayWidth = Math.round(imgData.width * scale);
      const displayHeight = Math.round(imgData.height * scale);
      children.push(
        new Paragraph({
          children: [
            new ImageRun({
              data: imgData.data,
              transformation: { width: displayWidth, height: displayHeight },
            }),
          ],
          spacing: { after: 100 },
        })
      );
    } else {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: "[Timeline image - generate from the application]", italics: true, color: "888888" })],
        })
      );
    }

    if (overview.legendEntries.length > 0) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: "Legend", bold: true })],
          spacing: { before: 80, after: 30 },
        })
      );
      overview.legendEntries.forEach((entry) => {
        children.push(
          new Paragraph({
            bullet: { level: 0 },
            children: [new TextRun({ text: entry.label })],
            spacing: { after: 20 },
          })
        );
      });
    }

    if (overview.keySteps.length > 0) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: "Key timeline checkpoints", bold: true })],
          spacing: { before: 80, after: 50 },
        })
      );
      overview.keySteps.slice(0, 8).forEach((step) => {
        children.push(
          new Paragraph({
            text: `Day ${step.day}: ${step.label}`,
            bullet: { level: 0 },
            spacing: { after: 30 },
          })
        );
      });
    }
  });

  // Section 24
  children.push(sectionTitle("Section 24: Preparation of animals"));
  children.push(
    new Paragraph(
      "Assessment of the preparation of animals for the experiment: description of screening examination, preparation to the experimental conditions, method of marking or identification and combination with the method of genotyping."
    )
  );

  formData.forEach((exp, index) => {
    const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
    const parts = [];
    const acclimDays = parseInt(exp.acclimatationDays || "0", 10);
    if (exp.acclimatation === "yes" && acclimDays > 0) {
      parts.push(`Acclimatation: ${acclimDays} days`);
    }
    const prepDays = parseInt(exp.preparationDays || "0", 10);
    const prepLabels = {
      special_diet: "Special diet",
      food_restriction: "Food restriction",
      water_restriction: "Water restriction",
      antibiotics_treatment: "Antibiotics treatment in water",
      tamoxifen_treatment: "Tamoxifen treatment in food/gels",
      other_preparation: "Other special preparation",
    };
    if (exp.preparation && exp.preparation !== "no_preparation" && prepDays > 0) {
      const prepLabel = prepLabels[exp.preparation] || "Special preparation";
      parts.push(`${prepLabel}: ${prepDays} days`);
    }
    const line = parts.length > 0 ? parts.join("; ") : "No acclimatation or special preparation.";
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${expLabel}: `, bold: true, color: "CC0044" }),
          new TextRun({ text: line, color: "CC0000" }),
        ],
        spacing: { after: 40 },
      })
    );
  });

  // Section 25 - Interventions Table
  children.push(sectionTitle("Section 25: Procedures / manipulations"));
  children.push(
    new Paragraph(
      "Assessment of licence conditions and suitability of the method: indicate the details of the various manipulations or interventions on the animal."
    )
  );
  formData.forEach((exp, index) => {
    const overview = buildExperimentTimelineOverview(exp, index);
    if (overview.procedureEntries.length === 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${overview.experimentLabel}: no procedures defined.`,
              italics: true,
              color: "666666",
            }),
          ],
          spacing: { after: 60 },
        })
      );
      return;
    }

    children.push(
      new Paragraph({
        children: [new TextRun({ text: overview.experimentLabel, bold: true, color: "CC0044" })],
        spacing: { before: 120, after: 60 },
      })
    );

    overview.procedureEntries.forEach((entry) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Procedure ${entry.index + 1}: ${entry.typeLabel}`,
              bold: true,
              color: "CC0000",
            }),
          ],
          spacing: { after: 30 },
        })
      );
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Method: ", bold: true }),
            new TextRun({ text: entry.methodLabel }),
          ],
          spacing: { after: 20 },
        })
      );
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Timing: ", bold: true }),
            new TextRun({ text: entry.scheduleLabel }),
          ],
          spacing: { after: 20 },
        })
      );
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Timeline days: ", bold: true }),
            new TextRun({ text: entry.dayLabel }),
          ],
          spacing: { after: 50 },
        })
      );
    });

    if (exp.section25Notes && exp.section25Notes.trim()) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: "Additional procedure notes:", bold: true })],
          spacing: { after: 20 },
        })
      );
      children.push(
        new Paragraph({
          children: [new TextRun({ text: exp.section25Notes.trim() })],
          spacing: { after: 60 },
        })
      );
    }
  });

  // Section 26
  children.push(sectionTitle("Section 26: Anaesthesia and/or analgesia"));
  children.push(
    new Paragraph(
      "Indicate the medications to be used, the dose and volume, the intended routes of administration, and consider the need for continuing administration."
    )
  );
  children.push(
    new Paragraph({
      children: [new TextRun({
        text: "[Complete this section manually: list the anaesthetic and/or analgesic agents, doses, volumes, and routes of administration for each procedure that requires them.]",
        italics: true,
        color: "888888",
      })],
      spacing: { after: 60 },
    })
  );

  // Section 27
  children.push(sectionTitle("Section 27: Methods of euthanasia"));
  children.push(
    new Paragraph(
      "Indicate the method of euthanasia, stating the substance used, the dose and route of administration, and the procedure to ensure the death of the animal."
    )
  );
  formData.forEach((exp, index) => {
    const terminalMethods = [
      "Euthanasia",
      "Perfusion, fixation and euthanasia",
      "Blood collection and euthanasia",
    ];

    const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
    exp.interventions.forEach((interv) => {
      if (terminalMethods.includes(interv.interventionMethod)) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `${expLabel}: `, bold: true, color: "CC0044" }),
              new TextRun({ text: `Method of euthanasia: ${interv.interventionMethod}`, color: "CC0000" }),
            ],
            spacing: { after: 40 },
          })
        );
      }
    });
  });

  // Section 29
  children.push(sectionTitle("Section 29: Experimental set-up and study design"));
  children.push(
    new Paragraph(
      "Description of the study design and planning: number of animals per experiment or series of experiments, number of groups, and number of animals per group. Include a description of each group. Specific considerations include allocation concealment or randomization, blinding, sample size calculation rationale, in- and exclusion criteria, definition of primary outcome variables, and the statistical analysis plan."
    )
  );
  children.push(new Paragraph(""));

  children.push(buildSection29Table(formData));
  children.push(new Paragraph(""));

  formData.forEach((exp, index) => {
    const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
    children.push(
      new Paragraph({
        children: [new TextRun({ text: expLabel, bold: true, color: "CC0044" })],
        spacing: { before: 150, after: 50 },
      })
    );

    const fields = [
      ["Experimental design", exp.experimentalDesign],
      ["Randomisation / Assignment", exp.randomization],
      ["Blinding", exp.blinding],
      ["Inclusion criteria", exp.inclusionCriteria],
      ["Exclusion criteria", exp.exclusionCriteria],
      ["Primary endpoints", exp.primaryEndpoints],
      ["Statistical analysis plan", exp.statisticalAnalysis],
    ];

    fields.forEach(([label, value]) => {
      if (value && String(value).trim()) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `${label}: `, bold: true }),
              new TextRun({ text: value, color: "CC0000" }),
            ],
            spacing: { after: 60 },
          })
        );
      }
    });
  });

  // Section 30
  children.push(sectionTitle("Section 30: Justification of animal numbers"));
  children.push(
    new Paragraph(
      "Indicate the reasons for the planned numbers of animals, including the method used for statistical analysis. If possible, indicate the test parameters (statistical coefficients, level of significance, power)."
    )
  );
  formData.forEach((exp, index) => {
    const summary = buildSection30Summary(exp, index);
    if (!summary.hasContent) return;

    children.push(
      new Paragraph({
        children: [new TextRun({ text: summary.experimentLabel, bold: true, color: "CC0044" })],
        spacing: { before: 150, after: 50 },
      })
    );

    summary.lines.forEach((line) => {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: line, color: "CC0000" })],
          spacing: { after: 50 },
        })
      );
    });
  });

  // Section 32
  children.push(sectionTitle("Section 32: Downgraded husbandry conditions"));
  children.push(
    new Paragraph(
      "Details and reasons for any deviations from conditions in which animals are kept as defined in the Animal Welfare Ordinance."
    )
  );
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "[Complete this section manually in the Word document.]", italics: true, color: "888888" })],
      spacing: { after: 60 },
    })
  );

  // Section 33
  children.push(sectionTitle("Section 33: Effects on the animals"));
  children.push(
    new Paragraph(
      "Assessment of all expected adverse effects on the animals and of further strain imposed on the animals through humiliation, major interference in their appearance or capabilities, or excessive instrumentalisation."
    )
  );
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "[Complete this section manually in the Word document.]", italics: true, color: "888888" })],
      spacing: { after: 60 },
    })
  );

  // Section 34
  children.push(sectionTitle("Section 34: Monitoring of well-being"));
  children.push(
    new Paragraph(
      "Assessment of the monitoring and documentation: indicate the criteria for intervention and termination (humane endpoints), the frequency of checks, who carries out checks, and how monitoring is documented."
    )
  );
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "[Complete this section manually in the Word document.]", italics: true, color: "888888" })],
      spacing: { after: 60 },
    })
  );

  // Section 35
  children.push(sectionTitle("Section 35: Refinement"));
  children.push(
    new Paragraph(
      "Assessment concerning implementing rules under the specific experimental conditions. Indicate the details of mitigating measures or reasons for not using such measures to reduce stress or minimise harm to the animals."
    )
  );
  children.push(
    new Paragraph({
      children: [new TextRun({ text: "[Complete this section manually in the Word document.]", italics: true, color: "888888" })],
      spacing: { after: 60 },
    })
  );

  // Section 36
  children.push(sectionTitle("Section 36: Distribution by degree of severity"));
  children.push(
    new Paragraph(
      "Summary evaluation of the expected maximum stress on the animals. Indicate the expected maximum degree of severity for each animal category and group."
    )
  );
  children.push(new Paragraph(""));
  children.push(buildSection36Table(formData));
  children.push(new Paragraph(""));

  formData.forEach((exp, index) => {
    const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
    const maxSeverity = getMaxDeclaredSeverity(exp);
    const severityWarnings = getExperimentSeverityWarnings(exp);
    const justification = String(exp.severityJustification || "").trim();

    children.push(
      new Paragraph({
        children: [new TextRun({ text: expLabel, bold: true, color: "CC0044" })],
        spacing: { before: 120, after: 50 },
      })
    );

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: "Declared maximum severity: ", bold: true }),
          new TextRun({ text: maxSeverity?.label || "Not specified" }),
        ],
        spacing: { after: 40 },
      })
    );

    if (justification) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Severity justification: ", bold: true }),
            new TextRun({ text: justification }),
          ],
          spacing: { after: 40 },
        })
      );
    }

    severityWarnings.forEach((warning) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Review note: ${warning.message}`,
              italics: true,
              color: warning.severity === "warning" ? "CC6600" : "666666",
            }),
          ],
          spacing: { after: 30 },
        })
      );
    });
  });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: "Times New Roman", size: 22 },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 },
        },
      },
      children,
    }],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Form_${projectName}.docx`);
};

export default generateFullDocx;
